import type { Equation, EquationDiagnostic } from "../equation";
import {
  analyzeEquationCapabilities,
  type EquationCapabilities,
} from "../capability-analysis";
import type { Dimension } from "@src/math/types";

export interface EquationViewModel {
  readonly capabilities: EquationCapabilities<Dimension>;
  readonly trayDiagnostics: readonly EquationDiagnostic[];
  readonly diagnosticsFor: (
    nodeId: string,
    field: "matrix-entry" | "vector-coordinate",
    index: number,
  ) => readonly EquationDiagnostic[];
}

const models = new WeakMap<object, EquationViewModel>();

export function getEquationViewModel<D extends Dimension>(
  equation: Equation<D>,
): EquationViewModel {
  const cached = models.get(equation);
  if (cached !== undefined) return cached;
  const diagnostics =
    equation.evaluation.kind === "stale" ? equation.evaluation.diagnostics : [];
  const global: EquationDiagnostic[] = [];
  const byField = new Map<string, EquationDiagnostic[]>();
  const byEntry = new Map<string, EquationDiagnostic[]>();
  for (const diagnostic of diagnostics) {
    const location = diagnostic.location;
    if (location === undefined) {
      global.push(diagnostic);
      continue;
    }
    const fieldKey = key(location.nodeId, location.field);
    const target = location.index === undefined ? byField : byEntry;
    const targetKey =
      location.index === undefined
        ? fieldKey
        : `${fieldKey}:${String(location.index)}`;
    const values = target.get(targetKey) ?? [];
    values.push(diagnostic);
    target.set(targetKey, values);
  }
  const model: EquationViewModel = {
    capabilities: analyzeEquationCapabilities(equation),
    trayDiagnostics: global,
    diagnosticsFor: (nodeId, field, index) => [
      ...(byField.get(key(nodeId, field)) ?? []),
      ...(byEntry.get(`${key(nodeId, field)}:${String(index)}`) ?? []),
    ],
  };
  models.set(equation, model);
  return model;
}

function key(nodeId: string, field: string): string {
  return `${nodeId}:${field}`;
}
